import type { BrandRecord } from '@navio/nsdk';
import type { z } from 'zod';

export type DiscountBrandValues = {
  limitToBrands: boolean;
  applicableBrandIds: number[];
};

export const BRAND_NARROWING_RESET_NOTICE =
  'Products and tags were cleared because they may not be sold under the selected brands. Pick them again.';

export const DISCOUNT_BRANDS_LABEL = 'Valid at';
export const DISCOUNT_BRANDS_TOOLTIP =
  'Limit this discount to bookings made under the selected brands. Brands added to the marketplace later are not included automatically.';

export const discountBrandOptions = (brand: Pick<BrandRecord, 'id' | 'name' | 'includedBrands'> | undefined) =>
  brand == null || brand.includedBrands.length === 0
    ? []
    : [brand, ...brand.includedBrands].map(({ id, name }) => ({ value: id, label: name }));

// null stands for every brand the campaign reaches.
const effectiveBrandIds = ({ limitToBrands, applicableBrandIds }: DiscountBrandValues): number[] | null =>
  limitToBrands && applicableBrandIds.length > 0 ? applicableBrandIds : null;

// Narrowing can leave picked products or tags outside the new brands; widening never can.
export const isBrandNarrowing = (previous: DiscountBrandValues, next: DiscountBrandValues): boolean => {
  const nextIds = effectiveBrandIds(next);
  if (nextIds == null) {
    return false;
  }
  const previousIds = effectiveBrandIds(previous);
  return previousIds == null || previousIds.some((id) => !nextIds.includes(id));
};

export const catalogOrganizationsForBrands = <T extends { brandId: number }>(
  organizations: T[],
  values: DiscountBrandValues
): T[] => {
  const brandIds = effectiveBrandIds(values);
  return brandIds == null
    ? organizations
    : organizations.filter((organization) => brandIds.includes(organization.brandId));
};

export const discountBrandValuesFromIds = (brandIds: number[]): DiscountBrandValues => ({
  limitToBrands: brandIds.length > 0,
  applicableBrandIds: brandIds
});

export const discountBrandIdsForSubmit = ({ limitToBrands, applicableBrandIds }: DiscountBrandValues): number[] =>
  limitToBrands ? applicableBrandIds : [];

export const refineDiscountBrands = (value: DiscountBrandValues, ctx: z.RefinementCtx): void => {
  if (value.limitToBrands && value.applicableBrandIds.length === 0) {
    ctx.addIssue({ code: 'custom', message: 'Pick at least one brand', path: ['applicableBrandIds'] });
  }
};
